/**
 * Background upload of pre-apply ("before") socket blobs.
 *
 * Contract with the apply path: **never block, never throw.** `enqueue` returns
 * immediately; the pack + PUT + finalize happen off the apply's critical path.
 * The local IndexedDB copy is written first, so Undo works the instant an apply
 * finishes even if the network is slow, offline, or the endpoints do not exist
 * yet on this server.
 *
 * Serialised, one upload at a time. Packing a socket allocates a multi-megabyte
 * buffer and the PUT saturates the connection; running several concurrently while
 * the surgeon is still working the tool would be felt in the viewport.
 *
 * Write-once per (step, tool): the manifest's `beforeByTool` entry is claimed
 * synchronously at enqueue time, so a tool re-applied five times still costs one
 * blob. See the note in `undoManifestStore` for why that is the right semantic.
 *
 * The server endpoints this uses are not deployed yet — the contract is specified
 * in `bengaitlabserp/docs/undo-api.md`. Until they exist the queue writes the local
 * copy and stops, which is why undo keeps working same-device regardless.
 */
import { loadBkDesignOrderContext } from '../../../shared/lib/bkDesignOrderContext';
import {
  createBkDesignUndoPresignedUpload,
  finalizeBkDesignUndoUpload,
  putBytesToPresignedUrl,
  isUndoApiUnavailable,
} from '../../../shared/services/erpApiService';
import { packMeshBlob } from './meshBlob';
import { putBeforeMesh } from './beforeMeshCache';
import useUndoManifestStore, { BEFORE_KIND } from './undoManifestStore';
import { needsBeforeMesh } from './undoStrategy';

const LOG_PREFIX = '[BeforeMeshUploader]';

/**
 * Set once the server answers "no such method". Everything keeps working from
 * the local cache; we simply stop making requests that cannot succeed.
 */
let serverUnavailable = false;

/** @type {Array<{ orderKey: string, salesOrder: string, customer: string, stepId: string, toolId: string, payload: object }>} */
const queue = [];
let draining = false;

/** Resolves when the queue is empty — used by tests and the pre-navigation flush. */
let idleResolvers = [];

export function isUndoServerAvailable() {
  return !serverUnavailable;
}

/** Test seam / recovery after a deploy that adds the endpoints. */
export function resetUndoServerAvailability() {
  serverUnavailable = false;
}

function currentOrder() {
  const ctx = loadBkDesignOrderContext();
  if (!ctx?.sales_order || !ctx?.customer) return null;
  return {
    orderKey: `${ctx.sales_order}:${ctx.customer}`,
    salesOrder: ctx.sales_order,
    customer: ctx.customer,
  };
}

/**
 * Claim the before-slot for this (step, tool) and schedule the write.
 *
 * Call this *before* the tool mutates the socket, with the payload the in-memory
 * undo pool just captured — that payload is the pre-apply state and is cheap to
 * hand over (it is already cloned).
 *
 * @param {string} stepId
 * @param {string} toolId
 * @param {{ geometry: import('three').BufferGeometry, position?: number[], quaternion?: number[], scale?: number[], meshName?: string, userData?: object, materialData?: object|null }} payload
 * @returns {boolean} whether a write was scheduled
 */
export function enqueueBeforeMesh(stepId, toolId, payload) {
  if (!stepId || !toolId || !payload?.geometry) return false;
  if (!needsBeforeMesh(toolId)) return false;

  const manifest = useUndoManifestStore.getState();

  // Write-once: an earlier apply on this step already stored the pre-apply socket.
  if (manifest.hasBeforeRef(stepId, toolId)) return false;

  const order = currentOrder();
  const vertexCount = payload.geometry.attributes?.position?.count ?? 0;

  // First apply on the step: the pre-apply socket is the previous step's finished
  // checkpoint, which is already on the server. Record a pointer, store nothing.
  if (manifest.isStepUntouched(stepId)) {
    manifest.setBeforeRef(stepId, toolId, {
      kind: BEFORE_KIND.PREV_STEP_CHECKPOINT,
      vertexCount,
      uploaded: true,
    });
    return true;
  }

  if (!order) {
    // No order context (local-only design): the in-memory pool and the step scene
    // cache still cover undo for this session.
    return false;
  }

  manifest.setBeforeRef(stepId, toolId, {
    kind: BEFORE_KIND.BLOB,
    vertexCount,
    uploaded: false,
  });

  queue.push({ ...order, stepId, toolId, payload });
  drain();
  return true;
}

async function drain() {
  if (draining) return;
  draining = true;

  while (queue.length > 0) {
    const job = queue.shift();
    try {
      await runJob(job);
    } catch (e) {
      // Local copy may still have landed; undo degrades to this device only.
      console.warn(`${LOG_PREFIX} ${job.stepId}/${job.toolId} failed`, e);
    }
  }

  draining = false;
  const resolvers = idleResolvers;
  idleResolvers = [];
  for (const resolve of resolvers) resolve();
}

async function runJob(job) {
  const { orderKey, salesOrder, customer, stepId, toolId, payload } = job;

  // Packing walks the whole geometry — give the renderer a frame first so an
  // apply that just finished still paints smoothly.
  await nextFrame();

  const buffer = packMeshBlob(payload);
  if (!buffer) return;

  const vertexCount = payload.geometry?.attributes?.position?.count ?? null;

  // Local first: this is what makes undo instant, and it is the fallback when the
  // upload cannot complete.
  await putBeforeMesh(orderKey, stepId, toolId, buffer, { vertexCount });

  if (serverUnavailable) return;

  try {
    const presign = await createBkDesignUndoPresignedUpload({
      salesOrderId: salesOrder,
      customer,
      step: stepId,
      toolId,
      bytes: buffer.byteLength,
    });

    const uploadUrl = presign?.upload_url;
    const fileKey = presign?.file_key;
    if (!uploadUrl || !fileKey) {
      console.warn(`${LOG_PREFIX} presign for ${stepId}/${toolId} returned no target`);
      return;
    }

    await putBytesToPresignedUrl(uploadUrl, new Blob([buffer], {
      type: 'application/octet-stream',
    }));

    await finalizeBkDesignUndoUpload({
      salesOrderId: salesOrder,
      customer,
      step: stepId,
      toolId,
      file_key: fileKey,
      metadata: {
        bytes: buffer.byteLength,
        vertex_count: vertexCount,
        manifest: useUndoManifestStore.getState().serializeForStep(stepId),
      },
    });

    useUndoManifestStore.getState().markBeforeUploaded(stepId, toolId, {
      key: fileKey,
      bytes: buffer.byteLength,
    });
  } catch (e) {
    if (isUndoApiUnavailable(e)) {
      serverUnavailable = true;
      console.warn(
        `${LOG_PREFIX} undo endpoints are not deployed on this server — ` +
          'keeping before-meshes device-local for the rest of the session',
      );
      return;
    }
    throw e;
  }
}

/**
 * Resolve once every queued upload has settled. Called before navigating away
 * from a step so the checkpoint metadata is written with accurate `uploaded`
 * flags rather than optimistic ones.
 *
 * @param {number} [timeoutMs] give up waiting (uploads continue in background)
 * @returns {Promise<boolean>} true if the queue drained, false on timeout
 */
export function flushBeforeMeshQueue(timeoutMs = 8000) {
  if (!draining && queue.length === 0) return Promise.resolve(true);
  return new Promise((resolve) => {
    let settled = false;
    const done = (value) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };
    idleResolvers.push(() => done(true));
    setTimeout(() => done(false), timeoutMs);
  });
}

/** Pending upload count — surfaced in diagnostics. */
export function pendingBeforeMeshUploads() {
  return queue.length + (draining ? 1 : 0);
}

function nextFrame() {
  return new Promise((resolve) => {
    if (typeof requestAnimationFrame === 'function') requestAnimationFrame(() => resolve());
    else setTimeout(resolve, 0);
  });
}

/** Exposed so the loading overlay can explain a slow navigation. */
export function describeUploadBacklog() {
  const n = pendingBeforeMeshUploads();
  return n === 0 ? null : { pending: n };
}
